import { Typography } from "@/components/common/Typography/Typography"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import type { DataRoomSummary as DataRoomSummaryModel } from "@/types/dataRoom"

interface DataRoomSummaryProps {
  summary: DataRoomSummaryModel | undefined
  isLoading: boolean
}

/** One quiet line under the title that says where the review stands. It
 * deliberately carries no progress bar: "1 of 1 accepted" would look like a
 * finished onboarding while most required documents are still missing, and
 * the bar would move backwards with every new upload. What is still required
 * lives in the checklist, not here. */
export const DataRoomSummary: React.FC<DataRoomSummaryProps> = ({
  summary,
  isLoading,
}) => {
  const { translate } = useTranslation()
  if (isLoading) {
    return (
      <div
        className="h-3 w-56 animate-pulse rounded bg-grey4/60"
        data-testid="data-room-summary-skeleton"
      />
    )
  }

  const total = summary?.total ?? 0
  const accepted = summary?.accepted ?? 0
  const inReview = summary?.inReview ?? 0
  const changesRequested = summary?.changesRequested ?? 0

  const parts =
    total === 0
      ? [translate("DATA_ROOM/SUMMARY_NONE")]
      : [
          translate("DATA_ROOM/SUMMARY_ACCEPTED_OF", {
            accepted,
            total,
          }),
          inReview > 0
            ? translate("DATA_ROOM/SUMMARY_IN_REVIEW", { count: inReview })
            : null,
          changesRequested > 0
            ? translate("DATA_ROOM/SUMMARY_CHANGES", {
                count: changesRequested,
              })
            : null,
        ].filter(Boolean)

  return (
    <span data-testid="data-room-summary">
      <Typography variant="textS" className="text-grey5">
        {parts.join(" · ")}
      </Typography>
    </span>
  )
}
